import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Star, Pause, Play, X, Save } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// displayIndex -> JavaScript Date.getDay() value stored in `weekday`
const DAY_TO_WEEKDAY = [1, 2, 3, 4, 5, 6, 0];

const EMPTY = {
  title: "",
  time: "",
  location: "",
  description: "",
  rsvp_url: "",
  payment_url: "",
  details_url: "",
  canceled: false,
  highlight: false,
  display_order: 0,
};

/** Strip empty strings so PATCH doesn't clobber optional fields with "". */
function clean(payload) {
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v === "" || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

export default function AdminWeekly() {
  const { authedClient } = useAuth();
  const [tab, setTab] = useState("recurring"); // "recurring" | "overrides" | "header"
  const [recurring, setRecurring] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [hero, setHero] = useState({ eyebrow: "", title: "" });
  const [loading, setLoading] = useState(true);

  // Editors
  const [editingRec, setEditingRec] = useState(null); // full item or "new"
  const [editingOv, setEditingOv] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const client = authedClient();
      const [rec, ov, eyebrow, title] = await Promise.all([
        client.get("/weekly/recurring"),
        client.get("/weekly/overrides"),
        client.get("/content/weekly.hero.eyebrow"),
        client.get("/content/weekly.hero.title"),
      ]);
      setRecurring(rec.data);
      setOverrides(ov.data);
      setHero({
        eyebrow: eyebrow.data?.body || "",
        title: title.data?.body || "",
      });
    } catch (e) {
      toast.error("Could not load Weekly Calendar data");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---------- Recurring ----------
  const saveRec = async (item) => {
    const body = { ...item, weekday: Number(item.weekday), display_order: Number(item.display_order || 0) };
    try {
      if (item.id) {
        await authedClient().patch(`/admin/weekly/recurring/${item.id}`, body);
        toast.success("Recurring item updated");
      } else {
        await authedClient().post("/admin/weekly/recurring", body);
        toast.success("Recurring item added");
      }
      setEditingRec(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeRec = async (id) => {
    if (!window.confirm("Delete this recurring item?")) return;
    try {
      await authedClient().delete(`/admin/weekly/recurring/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleActive = async (item) => {
    try {
      await authedClient().patch(`/admin/weekly/recurring/${item.id}`, { ...item, active: !item.active });
      loadAll();
    } catch {
      toast.error("Toggle failed");
    }
  };

  // ---------- Overrides ----------
  const saveOv = async (item) => {
    const body = { ...item, display_order: Number(item.display_order || 0) };
    try {
      if (item.id) {
        await authedClient().patch(`/admin/weekly/overrides/${item.id}`, body);
        toast.success("One-off updated");
      } else {
        await authedClient().post("/admin/weekly/overrides", body);
        toast.success("One-off added");
      }
      setEditingOv(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeOv = async (id) => {
    if (!window.confirm("Delete this one-off item?")) return;
    try {
      await authedClient().delete(`/admin/weekly/overrides/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---------- Hero blocks ----------
  const saveHero = async () => {
    try {
      await Promise.all([
        authedClient().put("/admin/content/weekly.hero.eyebrow", { body: hero.eyebrow }),
        authedClient().put("/admin/content/weekly.hero.title", { body: hero.title }),
      ]);
      toast.success("Header text saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="admin-weekly-page">
      <div className="mb-6">
        <p className="eyebrow mb-2">★ ADMIN</p>
        <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Weekly Calendar</h1>
        <p className="text-sm text-[color:var(--ink-soft)] mt-1">
          Everything the Weekly Calendar page shows - recurring items, one-off notes, and the hero text.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[color:var(--line)] mb-6" data-testid="admin-weekly-tabs">
        {[
          ["recurring", "Recurring items", recurring.length],
          ["overrides", "One-off items", overrides.length],
          ["header", "Header text", null],
        ].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`px-4 py-2.5 border-b-2 text-sm font-medium transition-colors ${
              tab === key
                ? "border-[color:var(--blue-deep)] text-[color:var(--blue-deep)]"
                : "border-transparent text-[color:var(--ink-soft)] hover:text-[color:var(--blue-deep)]"
            }`}
          >
            {label}
            {count !== null && count > 0 && (
              <span className="ml-2 text-xs bg-[color:var(--cream)] text-[color:var(--blue-deep)] px-2 py-0.5 rounded-full">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && <p className="text-center py-8 text-[color:var(--muted)]">Loading…</p>}

      {/* ---------------- Recurring tab ---------------- */}
      {!loading && tab === "recurring" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setEditingRec({ ...EMPTY, weekday: 5, active: true })}
              className="btn-primary"
              data-testid="admin-weekly-new-recurring"
            >
              <Plus className="w-4 h-4" /> Add recurring item
            </button>
          </div>

          {editingRec && (
            <RecurringForm
              value={editingRec}
              onSave={saveRec}
              onCancel={() => setEditingRec(null)}
            />
          )}

          {DAYS.map((day, i) => {
            const wd = DAY_TO_WEEKDAY[i];
            const items = recurring.filter((r) => r.weekday === wd);
            if (items.length === 0) return null;
            return (
              <div key={day} className="mb-6" data-testid={`recurring-${day.toLowerCase()}`}>
                <h3 className="font-display text-lg text-[color:var(--blue-ink)] mb-2">{day}</h3>
                <div className="space-y-2">
                  {items.map((it) => (
                    <ItemRow
                      key={it.id}
                      item={it}
                      onEdit={() => setEditingRec(it)}
                      onDelete={() => removeRec(it.id)}
                      onToggle={() => toggleActive(it)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {recurring.length === 0 && !editingRec && (
            <p className="text-center py-12 text-[color:var(--muted)]">
              No recurring items yet. Click <em>Add recurring item</em> to create one.
            </p>
          )}
        </div>
      )}

      {/* ---------------- Overrides tab ---------------- */}
      {!loading && tab === "overrides" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setEditingOv({ ...EMPTY, date: new Date().toISOString().slice(0, 10) })}
              className="btn-primary"
              data-testid="admin-weekly-new-override"
            >
              <Plus className="w-4 h-4" /> Add one-off item
            </button>
          </div>

          {editingOv && (
            <OverrideForm
              value={editingOv}
              onSave={saveOv}
              onCancel={() => setEditingOv(null)}
            />
          )}

          <div className="space-y-2">
            {overrides.map((it) => (
              <ItemRow
                key={it.id}
                item={{ ...it, weekday: null, __date: it.date }}
                onEdit={() => setEditingOv(it)}
                onDelete={() => removeOv(it.id)}
              />
            ))}
          </div>

          {overrides.length === 0 && !editingOv && (
            <p className="text-center py-12 text-[color:var(--muted)]">
              No one-off items. One-off notes are for a specific date only - they auto-delete
              30 days after their date.
            </p>
          )}
        </div>
      )}

      {/* ---------------- Header tab ---------------- */}
      {!loading && tab === "header" && (
        <div className="max-w-2xl">
          <div className="space-y-4 bg-[color:var(--cream)] rounded-2xl p-6">
            <label className="block">
              <span className="block text-sm font-medium mb-1 text-[color:var(--blue-ink)]">
                Eyebrow (small text above the title)
              </span>
              <input
                type="text"
                value={hero.eyebrow}
                onChange={(e) => setHero({ ...hero, eyebrow: e.target.value })}
                data-testid="hero-eyebrow-input"
                className="w-full rounded-lg border border-[color:var(--line)] bg-white px-3 py-2"
                placeholder="TIOB Weekly"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1 text-[color:var(--blue-ink)]">
                Title (main headline)
              </span>
              <input
                type="text"
                value={hero.title}
                onChange={(e) => setHero({ ...hero, title: e.target.value })}
                data-testid="hero-title-input"
                className="w-full rounded-lg border border-[color:var(--line)] bg-white px-3 py-2"
                placeholder="This week at Temple Israel."
              />
            </label>
            <p className="text-xs text-[color:var(--muted)]">
              The subtitle (week range + event count) is generated automatically.
            </p>
            <button onClick={saveHero} className="btn-primary" data-testid="hero-save">
              <Save className="w-4 h-4" /> Save header text
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Sub-components ----------------

function ItemRow({ item, onEdit, onDelete, onToggle }) {
  const inactive = item.active === false;
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border border-[color:var(--line)] px-4 py-3 bg-white ${
        inactive ? "opacity-60" : ""
      }`}
      data-testid={`item-row-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {item.highlight && <Star className="w-4 h-4 text-[color:var(--honey)]" />}
          {item.canceled && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">
              Canceled
            </span>
          )}
          <span className="font-medium text-[color:var(--blue-ink)] truncate">{item.title}</span>
        </div>
        <div className="text-xs text-[color:var(--ink-soft)] mt-1">
          {item.__date && <span className="mr-3">{item.__date}</span>}
          {item.time && <span className="mr-3">{item.time}</span>}
          {item.location && <span>{item.location}</span>}
        </div>
      </div>
      <div className="flex gap-1">
        {onToggle && (
          <button
            onClick={onToggle}
            title={inactive ? "Enable" : "Pause"}
            className="p-2 rounded-lg text-[color:var(--ink-soft)] hover:bg-[color:var(--cream)]"
            data-testid={`toggle-${item.id}`}
          >
            {inactive ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
        )}
        <button
          onClick={onEdit}
          className="p-2 rounded-lg text-[color:var(--blue-deep)] hover:bg-[color:var(--cream)]"
          data-testid={`edit-${item.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 rounded-lg text-red-600 hover:bg-red-50"
          data-testid={`delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function BaseFormFields({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Title *</span>
          <input required type="text" value={value.title || ""} onChange={(e) => set("title", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2"
            data-testid="form-title" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Time</span>
          <input type="text" placeholder="e.g. 7:00 PM" value={value.time || ""} onChange={(e) => set("time", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2"
            data-testid="form-time" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Location</span>
          <input type="text" value={value.location || ""} onChange={(e) => set("location", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Display order</span>
          <input type="number" value={value.display_order ?? 0} onChange={(e) => set("display_order", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
        </label>
      </div>
      <label className="block">
        <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Description</span>
        <textarea rows={3} value={value.description || ""} onChange={(e) => set("description", e.target.value)}
          className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">RSVP URL</span>
          <input type="url" value={value.rsvp_url || ""} onChange={(e) => set("rsvp_url", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Payment URL</span>
          <input type="url" value={value.payment_url || ""} onChange={(e) => set("payment_url", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Details URL</span>
          <input type="url" value={value.details_url || ""} onChange={(e) => set("details_url", e.target.value)}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2" />
        </label>
      </div>
      <div className="flex gap-6">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!value.highlight} onChange={(e) => set("highlight", e.target.checked)}
            data-testid="form-highlight" />
          <Star className="w-4 h-4 text-[color:var(--honey)]" /> Featured this week
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!value.canceled} onChange={(e) => set("canceled", e.target.checked)}
            data-testid="form-canceled" />
          Canceled
        </label>
      </div>
    </>
  );
}

function RecurringForm({ value, onSave, onCancel }) {
  const [form, setForm] = useState(value);
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  return (
    <form
      onSubmit={submit}
      className="mb-6 p-6 rounded-2xl bg-[color:var(--cream)] space-y-4"
      data-testid="admin-weekly-recurring-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-[color:var(--blue-ink)]">
          {form.id ? "Edit recurring item" : "New recurring item"}
        </h3>
        <button type="button" onClick={onCancel} className="p-1 text-[color:var(--muted)]">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Weekday *</span>
          <select
            value={form.weekday ?? 5}
            onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
            className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white"
            data-testid="form-weekday"
          >
            {DAYS.map((d, i) => (
              <option key={i} value={DAY_TO_WEEKDAY[i]}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Active</span>
          <label className="inline-flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Show this item on the Weekly Calendar
          </label>
        </label>
      </div>
      <BaseFormFields value={form} onChange={setForm} />
      <div className="flex gap-3">
        <button type="submit" className="btn-primary" data-testid="form-submit">
          <Save className="w-4 h-4" /> {form.id ? "Save changes" : "Create item"}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">
          Cancel
        </button>
      </div>
    </form>
  );
}

function OverrideForm({ value, onSave, onCancel }) {
  const [form, setForm] = useState(value);
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  return (
    <form
      onSubmit={submit}
      className="mb-6 p-6 rounded-2xl bg-[color:var(--cream)] space-y-4"
      data-testid="admin-weekly-override-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-[color:var(--blue-ink)]">
          {form.id ? "Edit one-off item" : "New one-off item"}
        </h3>
        <button type="button" onClick={onCancel} className="p-1 text-[color:var(--muted)]">
          <X className="w-5 h-5" />
        </button>
      </div>
      <label className="block max-w-xs">
        <span className="block text-xs font-medium mb-1 text-[color:var(--ink)]">Date *</span>
        <input
          required
          type="date"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2"
          data-testid="form-date"
        />
      </label>
      <BaseFormFields value={form} onChange={setForm} />
      <div className="flex gap-3">
        <button type="submit" className="btn-primary" data-testid="form-submit-override">
          <Save className="w-4 h-4" /> {form.id ? "Save changes" : "Create item"}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">
          Cancel
        </button>
      </div>
    </form>
  );
}
